// iOS-only StoreKit sheet for buying CONSUMABLE credit packs. Accessible to ALL
// tiers (Free included) — credit packs grant NO entitlement, so there is NO
// premium gate here. Apple 3.1.1 compliant: credits are sold ONLY via StoreKit /
// RevenueCat — never Asaas / PIX / aivyv.com (those stay web-only).
//
// The app NEVER grants credit locally. A successful purchase is recorded by
// RevenueCat; the revenuecat-webhook calls grant_credits_atomic server-side; we
// only poll user_credits and refresh the balance. If the grant hasn't landed
// within a short retry window we tell the user it's on the way.
//
// Prices come from StoreKit (pkg.product.priceString) — never hardcoded.
// Aesthetics mirror IosPaywall.tsx (the Premium StoreKit sheet).

import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, X, Coins } from "lucide-react";
import { toast } from "sonner";
import type { PurchasesOffering, PurchasesPackage } from "@revenuecat/purchases-capacitor";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/i18n/LanguageProvider";
import { isNativeIapEnabled } from "@/lib/platform";
import { getCreditOfferings, purchaseConsumable } from "@/lib/revenuecat";
import { creditsForProductId } from "@/config/credits";
import { supabase } from "@/integrations/supabase/client";

type LoadState = "loading" | "ready" | "error";

interface IosCreditSheetProps {
  open: boolean;
  onClose: () => void;
  /** Fired after a purchase whose credit grant was observed (or the retry window
   *  elapsed) so the opener can refresh its own balance display. */
  onPurchased?: () => void;
}

// Current credit balance in units (server-authoritative). Standalone (non-hook)
// so the post-purchase poll can call it repeatedly. Returns 0 on no user/error.
async function fetchBalanceUnits(): Promise<number> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return 0;
    const { data } = await supabase
      .from("user_credits")
      .select("balance_units")
      .eq("user_id", user.id)
      .maybeSingle();
    const v = (data as { balance_units?: number } | null)?.balance_units;
    return typeof v === "number" ? v : 0;
  } catch (err) {
    console.error("[IosCreditSheet] balance fetch failed:", err);
    return 0;
  }
}

// Short retry: the webhook grant is async, so poll a few times for the balance
// to climb above the pre-purchase value.
const POLL_ATTEMPTS = 3;
const POLL_INTERVAL_MS = 1200;

async function pollCreditIncrease(before: number): Promise<boolean> {
  for (let i = 0; i < POLL_ATTEMPTS; i++) {
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    if ((await fetchBalanceUnits()) > before) return true;
  }
  return false;
}

export default function IosCreditSheet({ open, onClose, onPurchased }: IosCreditSheetProps) {
  const { t, tf } = useLanguage();
  const [state, setState] = useState<LoadState>("loading");
  const [packages, setPackages] = useState<PurchasesPackage[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setState("loading");
    const off: PurchasesOffering | null = await getCreditOfferings();
    const pkgs = (off?.availablePackages ?? [])
      .slice()
      .sort((a, b) => a.product.price - b.product.price);
    if (pkgs.length === 0) {
      setState("error");
      return;
    }
    setPackages(pkgs);
    // Default to the middle pack (balanced) when 3 exist, else the first.
    setSelectedId(pkgs[Math.min(1, pkgs.length - 1)].identifier);
    setState("ready");
  }, []);

  useEffect(() => {
    if (open && isNativeIapEnabled()) load();
  }, [open, load]);

  // Defense in depth: never render off the IAP path even if a caller slips.
  if (!isNativeIapEnabled()) return null;

  const selectedPkg = packages.find((p) => p.identifier === selectedId) ?? null;

  const creditLabel = (pkg: PurchasesPackage): string => {
    const n = creditsForProductId(pkg.product.identifier);
    return n != null
      ? tf("credits.pack.credits", { n: String(n) })
      : pkg.product.title;
  };

  const handleBuy = async () => {
    if (!selectedPkg || processing) return;
    setProcessing(true);
    // Baseline BEFORE the purchase so the poll can detect the server-side grant.
    const before = await fetchBalanceUnits();
    const res = await purchaseConsumable(selectedPkg);
    if (res.cancelled) {
      setProcessing(false);
      return; // user backed out — silent
    }
    if (!res.success) {
      setProcessing(false);
      toast.error(t("credits.iap.purchaseError"));
      return;
    }
    // Purchase OK — credit is granted server-side. Do NOT credit locally; poll.
    const reflected = await pollCreditIncrease(before);
    setProcessing(false);
    onPurchased?.();
    if (reflected) toast.success(t("credits.success.title"));
    else toast.message(t("credits.iap.pending"));
    onClose();
  };

  const renderOption = (pkg: PurchasesPackage) => {
    const isSel = selectedId === pkg.identifier;
    return (
      <button
        key={pkg.identifier}
        type="button"
        onClick={() => setSelectedId(pkg.identifier)}
        className="w-full rounded-2xl px-4 py-3.5 flex items-center justify-between transition-all text-left"
        style={{
          background: isSel ? "hsl(187 72% 50% / 0.10)" : "rgba(255,255,255,0.03)",
          border: isSel
            ? "1.5px solid hsl(187 72% 50% / 0.65)"
            : "1.5px solid rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-4 h-4 rounded-full flex items-center justify-center shrink-0"
            style={{
              border: isSel
                ? "5px solid hsl(187 72% 55%)"
                : "2px solid rgba(255,255,255,0.25)",
            }}
          />
          <p className="font-display text-[14px] font-semibold text-foreground">
            {creditLabel(pkg)}
          </p>
        </div>
        <span className="font-body text-[13px] text-foreground/70">
          {pkg.product.priceString}
        </span>
      </button>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          style={{ background: "rgba(2, 6, 14, 0.82)", backdropFilter: "blur(12px)" }}
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="ios-credit-title"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full sm:w-auto sm:min-w-[400px] sm:max-w-md rounded-t-3xl sm:rounded-3xl ring-1 ring-cyan-500/20 overflow-hidden max-h-[85dvh] overflow-y-auto"
            style={{
              background:
                "linear-gradient(180deg, #050811 0%, #0a0e1a 60%, #050811 100%)",
              boxShadow: "0 -10px 60px rgba(0,0,0,0.55)",
            }}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t("paywall.moduleLocked.cta.close")}
              className="absolute top-3 right-3 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/[0.06] hover:bg-white/[0.10] transition-colors"
            >
              <X size={16} className="text-foreground/70" />
            </button>

            <div
              className="px-6 pt-9 flex flex-col items-center text-center"
              style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom, 1.5rem))" }}
            >
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                style={{
                  background:
                    "radial-gradient(circle at center, hsl(187 72% 50% / 0.22) 0%, hsl(187 72% 30% / 0.10) 70%)",
                  border: "1px solid hsl(187 72% 50% / 0.30)",
                }}
              >
                <Coins size={22} className="text-primary" />
              </div>

              <h2
                id="ios-credit-title"
                className="font-display text-[22px] font-semibold tracking-tight text-foreground leading-snug"
              >
                {t("credits.headline")}
              </h2>
              <p className="font-display text-[14px] text-muted-foreground leading-relaxed mt-2 max-w-sm">
                {t("credits.subtitle")}
              </p>

              {/* Pack options / states */}
              <div className="w-full mt-6 min-h-[120px] flex flex-col justify-center gap-2.5">
                {state === "loading" && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
                {state === "error" && (
                  <div className="flex flex-col items-center gap-3 py-4">
                    <p className="font-body text-[13px] text-muted-foreground">
                      {t("pricing.iap.loadError")}
                    </p>
                    <Button variant="outline" size="lg" onClick={load}>
                      {t("common.retry")}
                    </Button>
                  </div>
                )}
                {state === "ready" && packages.map(renderOption)}
              </div>

              {state === "ready" && (
                <div className="w-full flex flex-col gap-2 mt-5">
                  <Button
                    variant="hero"
                    size="xl"
                    className="w-full"
                    disabled={processing || !selectedPkg}
                    onClick={handleBuy}
                  >
                    {processing ? t("pricing.iap.processing") : t("credits.cta.buy")}
                  </Button>
                </div>
              )}

              {/* What the selected price buys — clear, store-localized. */}
              {state === "ready" && selectedPkg && (
                <p className="font-body text-[12px] text-foreground/70 leading-relaxed mt-4">
                  {creditLabel(selectedPkg)}
                  {" — "}
                  {selectedPkg.product.priceString}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
